import { t } from 'i18next';
import { camelCase } from 'lodash';

import { DEFAULT_COUNTRY } from 'addresses/constants';
import { IStateAutocomplete } from 'addresses/types';
import { IContactGroupAddress } from 'contacts/types';
import { ICorporateUserAutocomplete } from 'corporate/types';

import { ContactGroupAddressType } from './enums';
import { IQuoteAddressForm } from './types';

export const getDefaultValues = <T>(
  parent: T,
  type = ContactGroupAddressType.SHIP_TO
): IQuoteAddressForm => {
  const address = (parent as any)?.[`${camelCase(type)}Address`] || {};

  const defaultValues = {
    location: address,
    address: {
      line1: address?.line1,
      line2: address?.line2,
      city: address?.city,
      state: address?.stateId
        ? ({
            id: address?.stateId,
            name: address?.stateName,
            abbreviation: address?.stateAbbreviation,
          } as IStateAutocomplete)
        : (address?.state as string),

      country: address?.countryId
        ? {
            id: address?.countryId,
            name: address?.countryName,
            abbreviation: address?.countryAbbreviation,
          }
        : DEFAULT_COUNTRY,
      zip: address?.zip,
      name: address?.name,
    },
  };

  switch (type) {
    case ContactGroupAddressType.SHIP_TO:
      return {
        ...defaultValues,
        contact: {
          name: address?.contactName,
          companyName: address?.contactCompanyName,
        } as ICorporateUserAutocomplete,
        contactName: address?.contactName,
        contactPhone: address?.contactPhone,
        contactEmail: address?.contactEmail,
        contactCompanyName: address?.contactCompanyName,
      };
    case ContactGroupAddressType.BILL_TO:
    case ContactGroupAddressType.SOLD_TO:
    default:
      return defaultValues;
  }
};

export const getContactGroupAddressTitle = (type: ContactGroupAddressType) => {
  switch (type) {
    case ContactGroupAddressType.SHIP_TO:
      return t('titleEditShipToAddress');
    case ContactGroupAddressType.BILL_TO:
      return t('titleEditBillToAddress');
    case ContactGroupAddressType.SOLD_TO:
      return t('titleEditSoldToAddress');
    default:
      return t('titleEditContactGroupAddress');
  }
};

// Until the user re-picks a location, the form's location is the parent's current address, so its
// AR must be sent: an empty AR makes the back-end fall back to PROSPECT on the bill-to.
export const getAccountsReceivableId = (
  pickedAccountsReceivableId: string | undefined,
  location?: Pick<IContactGroupAddress, 'accountsReceivableId'> | null
): string => pickedAccountsReceivableId ?? location?.accountsReceivableId ?? '';
